import { ChatAdmin, ChatUser, ChatThinking } from "@pages/chatbot/components/Conversation";
import { getMessageGroupPosition, getMessageSpacingClass, isSameSenderGroup } from "./conversationGroupUtils";
import OnboardingFeatureGrid from "./OnboardingFeatureGrid";
import InstructionGuideGrid from "./InstructionGuideGrid";
import { CommandsList, ReconnectBar } from "@pages/chatbot/components";
import React, { useEffect, useState, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import { formatChatDate } from "@utils/index";
import { useTranslation } from "react-i18next";
import { ChatAgentType, type SocketMessageMeta } from "@constants/chat-agents";
import { LoadingOutlined } from "@ant-design/icons";
import "./Conversation.scss";
import { SenderType, type MessageButton } from "../../../../types/chat-message";
import type { ChatMessage } from "../../../../types";
import { ArrowDownIcon, NewIconChatbot, CskhHeaderIcon } from "@assets/icon";
import { chatService } from "../../../../services/chatService";
import type { ToastMessage } from '@hooks/useChat';
import {
  CHATBOT_TIP_ACTION_MESSAGE,
  CHATBOT_TIP_BUBBLE_MESSAGE,
  hasConsumedTipBubbleForWindow,
  markTipBubbleConsumed,
  pickRandomQuickSuggestionTips,
  pinQuickSuggestionToTop,
  type QuickSuggestion,
  type QuickSuggestionTip,
} from '@constants/onboardingFeatures';
import { PostMessageType } from '@constants/postMessage';
import { safePostMessage } from '@utils/index';
import { REACT_APP_ALLOWED_DOMAINS } from '../../../../config';
import { isPaymentAccountButtonHidden } from './paymentAccountUtils';


interface ConversationProps {
  connected: boolean;
  handleChatSubmit: (
    message: string, buttonId?: number, isHidden?: boolean, images?: undefined,
    component?: undefined, meta?: SocketMessageMeta,
  ) => void | Promise<void>;
  onReadyConversation: () => void;
  chatLog: any[];
  chatRef: React.RefObject<HTMLDivElement | null>;
  chatSessionVersion?: number;
  /** Tăng mỗi lần mở chatbot → refetch quick-suggestions theo quyền mới */
  quickSuggestionsRefreshKey?: number;
  /** Tăng mỗi lần đóng chatbot → thu gọn onboarding grid */
  welcomeResetKey?: number;
  hasChatHistory: boolean | null;
  activeAgent?: ChatAgentType;
  isThinking?: boolean;
  setIsThinking?: (value: boolean) => void;
  /** AI đang thinking / streaming / typewriter — dùng để đổi icon nút scroll-down */
  isBotResponding?: boolean;
  onScrollToBottom?: () => void;
  loadMoreHistory?: () => Promise<void>;
  isFetchingMore?: boolean;
  handleAgentChange?: (agent: ChatAgentType) => void;
  handleForwardToAgent?: (targetAgent: ChatAgentType, userQuestion: string | undefined, botMessage: ChatMessage | undefined, botMessageId?: string) => void;
  handleRateMessage?: (messageId: string, action: 'LIKE' | 'DISLIKE', dislikeReason?: string, reasonIds?: string[]) => Promise<void>;
  handleRetryUpload?: (message: ChatMessage) => Promise<void>;
  handleQuoteMessage?: (message: ChatMessage) => void;
  scrollToQuotedMessage?: (targetMessageId: string | number) => Promise<void>;
  loadNewerHistory?: () => Promise<void>;
  needsManualReconnect?: boolean;
  isReconnecting?: boolean;
  onManualReconnect?: () => void;
  commands: import('../../../../types/chat-command').DefaultCommand[];
  isCommandsLoading?: boolean;
  handleCommandClick: (
    command: string,
    id?: number,
    options?: { actionType?: string; actionValue?: string },
  ) => void;
  onConfirmStay?: (messageId: string, blockContents?: import('../../../../types/chat-message').BlockContent[]) => void;
  onConfirmLeave?: (messageId: string, pendingAgent: import('@constants/chat-agents').ChatAgentType) => void;
  setToastMessage?: (toast: NonNullable<ToastMessage>) => void;
  isAudioEnabled?: boolean;
  isBotSpeaking?: boolean;
  isAudioPlayerVisible?: boolean;
  playBotAudio?: (audio: string) => void;
  stopBotSpeaking?: () => void;
  /** Plus-menu feature key (e.g. `manual`) — drives new-chat welcome UI */
  selectedPlusFeatureKey?: string | null;
  instructions?: any[];
  isInstructionsLoading?: boolean;
  onSelectInstruction?: (item: any) => void;
}

const ALLOWED_DOMAINS: string[] = REACT_APP_ALLOWED_DOMAINS
  ? REACT_APP_ALLOWED_DOMAINS.split(',').map((d) => d.trim()).filter(Boolean)
  : ['localhost:3001', 'localhost:3000'];

const isAllowedParentOrigin = (origin: string) => {
  try {
    const url = new URL(origin);
    const originDomain = url.port ? `${url.hostname}:${url.port}` : url.hostname;
    return ALLOWED_DOMAINS.some(
      (domain) =>
        originDomain === domain ||
        url.hostname.endsWith(domain) ||
        (domain.startsWith('localhost') && url.hostname === 'localhost'),
    );
  } catch {
    return false;
  }
};


const CHAT_FOOTER_SPACER_PX = 84;
/** Question-focus padding budget as a fraction of the chat viewport (was 100%). */
const QUESTION_FOCUS_PADDING_RATIO = 0.4;

const Conversation: React.FC<ConversationProps> = ({
  handleChatSubmit,
  chatLog = [],
  chatRef,
  chatSessionVersion,
  quickSuggestionsRefreshKey = 0,
  welcomeResetKey = 0,
  hasChatHistory,
  activeAgent = ChatAgentType.AI,
  isThinking = false,
  setIsThinking,
  isBotResponding = false,
  loadMoreHistory,
  isFetchingMore = false,
  handleAgentChange,
  handleForwardToAgent,
  handleRateMessage,
  handleRetryUpload,
  handleQuoteMessage,
  scrollToQuotedMessage,
  loadNewerHistory,
  needsManualReconnect = false,
  isReconnecting = false,
  onManualReconnect,
  commands,
  isCommandsLoading,
  handleCommandClick,
  onConfirmStay,
  onConfirmLeave,
  setToastMessage,
  isAudioEnabled = true,
  isBotSpeaking = false,
  isAudioPlayerVisible = false,
  playBotAudio,
  stopBotSpeaking,
  selectedPlusFeatureKey = null,
  instructions = [],
  isInstructionsLoading = false,
  onSelectInstruction,
}) => {
  const { t } = useTranslation();
  const [userName, setUserName] = useState<string | null>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  /** Original list from GET /quick-suggestions (API order). Do not mutate on pin. */
  const [quickSuggestions, setQuickSuggestions] = useState<QuickSuggestion[]>([]);
  /** Tip currently shown on seller bubble — only affects display copy. */
  const [pinnedTip, setPinnedTip] = useState<Pick<
    QuickSuggestionTip,
    'featureKey' | 'title' | 'text'
  > | null>(null);
  const [isQuickSuggestionsLoading, setIsQuickSuggestionsLoading] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isFirstLoadRef = useRef(true);
  const scrollOffsetRef = useRef<number | null>(null);
  const lastUserMsgRef = useRef<HTMLDivElement | null>(null);
  const isRestoringScrollRef = useRef(false);
  const previousAgentRef = useRef<ChatAgentType | null>(null);
  const previousSessionVersionRef = useRef<number | null>(null);
  // Tracks whether we're in "question focused at top" mode
  const isQuestionFocusedRef = useRef(false);

  const displayQuickSuggestions = useMemo(() => {
    const next = pinnedTip
      ? pinQuickSuggestionToTop(quickSuggestions, pinnedTip)
      : quickSuggestions;

    if (pinnedTip) {
      console.log('[Welcome] Rearrange displayQuickSuggestions', {
        pinnedTip,
        original: quickSuggestions.map((item) => ({
          featureKey: item.featureKey,
          title: item.title,
          order: item.order,
        })),
        display: next.map((item) => ({
          featureKey: item.featureKey,
          title: item.title,
          order: item.order,
        })),
      });
    }

    return next;
  }, [quickSuggestions, pinnedTip]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = scrollContainerRef.current;
    if (container) {
      // Scroll to the end of the list (includes footer spacer)
      container.scrollTo({ top: container.scrollHeight, behavior });
      return;
    }
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  }, [chatRef]);

  // Helper: recalculate paddingBottom so question can stay near top
  // padding = max(16, focusBudget - contentBelowUserMsg)
  const recalcScrollPadding = useCallback(() => {
    const container = scrollContainerRef.current;
    const msgEl = lastUserMsgRef.current;
    if (!container || !msgEl || !isQuestionFocusedRef.current) return;

    // Content below user message = scrollHeight - current extra padding + original padding - msgBottom
    const currentPadding = parseInt(container.style.paddingBottom || '16', 10);
    const originalPadding = 16;
    const realScrollHeight = container.scrollHeight - currentPadding + originalPadding;
    const msgBottom = msgEl.offsetTop + msgEl.offsetHeight;
    const contentBelowMsg = realScrollHeight - msgBottom;

    const focusBudget = Math.round(container.clientHeight * QUESTION_FOCUS_PADDING_RATIO);
    const neededPadding = Math.max(originalPadding, focusBudget - contentBelowMsg);
    container.style.paddingBottom = `${neededPadding}px`;
  }, []);

  useEffect(() => {
    // Reset first load flag when agent changes
    if (previousAgentRef.current !== activeAgent || previousSessionVersionRef.current !== (chatSessionVersion ?? null)) {
      isFirstLoadRef.current = true;
      previousAgentRef.current = activeAgent;
      previousSessionVersionRef.current = chatSessionVersion ?? null;
      scrollOffsetRef.current = null;
      isRestoringScrollRef.current = false;
      // Reset question-focus mode and padding
      isQuestionFocusedRef.current = false;
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.paddingBottom = '16px';
      }
    }
    fetchUserInfo();
  }, [activeAgent, chatLog, chatSessionVersion]);

  useEffect(() => {
    // Quick suggestions + tip bubble only on Hỏi đáp (AI) agent
    if (activeAgent !== ChatAgentType.AI) {
      setQuickSuggestions([]);
      setPinnedTip(null);
      setIsQuickSuggestionsLoading(false);
      return;
    }

    if (hasChatHistory !== false) return;

    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    let cancelled = false;

    const fetchQuickSuggestions = async () => {
      setIsQuickSuggestionsLoading(true);
      try {
        const response = await chatService.getQuickSuggestions(token);
        if (cancelled) return;

        const payload = response?.data;
        const suggestions = Array.isArray(payload?.suggestions)
          ? payload.suggestions
          : [];

        if (payload?.showSuggestion === false) {
          setQuickSuggestions([]);
        } else {
          setQuickSuggestions(
            [...suggestions].sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0),
            ),
          );

          // Parent tip bubble: once per morning/afternoon window (user+shop).
          // In-chat grid above still uses the full suggestions list.
          if (!hasConsumedTipBubbleForWindow(token)) {
            const tips = pickRandomQuickSuggestionTips(
              suggestions,
              3,
              payload?.totalCount,
            );
            if (tips.length > 0) {
              safePostMessage({
                type: CHATBOT_TIP_BUBBLE_MESSAGE,
                payload: { tips },
              });
              markTipBubbleConsumed(token);
            }
          }
        }

      } catch (error) {
        if (!cancelled) {
          console.error('[Welcome] quick suggestions failed:', error);
        }
      } finally {
        if (!cancelled) {
          setIsQuickSuggestionsLoading(false);
        }
      }
    };

    fetchQuickSuggestions();

    return () => {
      cancelled = true;
    };
  }, [hasChatHistory, activeAgent, quickSuggestionsRefreshKey]);

  const handleSuggestionClick = useCallback(
    (
      suggestion: Pick<
        QuickSuggestion,
        'featureKey' | 'title' | 'actionType' | 'actionValue'
      >,
      clickType: string = 'MINI_UI',
    ) => {
      const token = sessionStorage.getItem('access_token');
      if (token && suggestion.featureKey) {
        void chatService
          .trackQuickSuggestion(token, {
            featureKey: suggestion.featureKey,
            clickType,
            device: 'web',
          })
          .catch(() => {
            // Track is best-effort; do not block card action
          });
      }

      handleCommandClick(suggestion.title, undefined, {
        actionType: suggestion.actionType,
        actionValue: suggestion.actionValue,
      });
    },
    [handleCommandClick],
  );

  useEffect(() => {
    setPinnedTip(null);
  }, [welcomeResetKey]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const incomingType = (event.data as { type?: string } | undefined)?.type;
      if (
        incomingType === PostMessageType.CHAT_TOGGLE ||
        incomingType === CHATBOT_TIP_ACTION_MESSAGE
      ) {
        console.log('[Welcome] postMessage received (launcher / tip)', {
          origin: event.origin,
          allowed: isAllowedParentOrigin(event.origin),
          data: event.data,
        });
      }

      if (!isAllowedParentOrigin(event.origin)) return;

      const raw = (event.data || {}) as {
        type?: string;
        payload?: Partial<QuickSuggestionTip> & { description?: string; executeAction?: boolean | string };
        data?: Partial<QuickSuggestionTip> & { description?: string; executeAction?: boolean | string };
        executeAction?: boolean | string;
        featureKey?: string;
        title?: string;
        text?: string;
        description?: string;
        actionType?: string;
        actionValue?: string;
      };

      if (raw.type !== CHATBOT_TIP_ACTION_MESSAGE) return;

      const payload = raw.payload || raw.data || raw;
      const executeAction =
        payload.executeAction ?? raw.executeAction ?? raw.data?.executeAction;
      const shouldExecute = executeAction !== false && executeAction !== 'false';

      const pin = {
        featureKey: payload.featureKey || '',
        title: payload.title || '',
        text: payload.text || payload.description || '',
      };

      if (!shouldExecute) {
        console.log('[Welcome] CHATBOT_TIP_ACTION pin only (executeAction=false)', pin);
        setPinnedTip(pin);
        return;
      }

      handleSuggestionClick(
        {
          featureKey: payload.featureKey || '',
          title: payload.title || payload.text || '',
          actionType: payload.actionType || 'CHATBOT',
          actionValue: payload.actionValue || '',
        },
        'BUBBLE_EVENT',
      );
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [handleSuggestionClick]);

  const fetchUserInfo = async () => {
    try {
      if (userName === null) {
        const token = sessionStorage.getItem('access_token');
        if (token) {
          try {
            const userInfoRes = await chatService.getUserInfo(token);
            setUserName(userInfoRes?.account?.full_name || '');
          } catch (e) {
            console.error('Failed to get user info', e);
            setUserName('');
          }
        } else {
          setUserName('');
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isFirstLoadRef.current || hasChatHistory === null || !chatLog || chatLog.length === 0) return;

    const triggerScroll = () => {
      if (container.offsetHeight > 0) {
        if (!isQuestionFocusedRef.current) {
          scrollToBottom('auto');
        }

        if (chatLog.length > 0) {
          lastMessageRef.current = chatLog[chatLog.length - 1];
        }

        setTimeout(() => {
          isFirstLoadRef.current = false;
        }, 300);
      }
    };

    const ro = new ResizeObserver(() => {
      if (isFirstLoadRef.current) {
        triggerScroll();
      } else if (!isQuestionFocusedRef.current && container && !isFetchingMore) {
        // Only auto-scroll when NOT in question-focus mode
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 250;
        if (isNearBottom) {
          scrollToBottom('auto');
        }
      }
    });
    ro.observe(container);

    triggerScroll();
    const timers = [
      setTimeout(triggerScroll, 100),
      setTimeout(triggerScroll, 500),
    ];

    return () => {
      ro.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [chatLog?.length, hasChatHistory, activeAgent, scrollToBottom]);

  useLayoutEffect(() => {
    // Restore scroll position after loading more history
    if (scrollOffsetRef.current !== null && scrollContainerRef.current && !isFetchingMore) {
      const container = scrollContainerRef.current;
      const scrollOffset = scrollOffsetRef.current;

      // Wait for DOM to update, then restore scroll
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const newScrollHeight = scrollContainerRef.current.scrollHeight;
          // Calculate new scroll position: maintain distance from bottom
          const newScrollTop = newScrollHeight - scrollOffset - container.clientHeight;
          container.scrollTop = Math.max(0, newScrollTop);
          scrollOffsetRef.current = null;
          isRestoringScrollRef.current = false;
        }
      });
      return; // Don't proceed to auto-scroll logic when restoring
    }
  }, [chatLog, isFetchingMore]);

  const lastMessageRef = useRef<any>(null);

  useLayoutEffect(() => {
    if (
      isFetchingMore ||
      scrollOffsetRef.current !== null ||
      !chatLog ||
      chatLog.length === 0
    ) {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    const lastMsg = chatLog[chatLog.length - 1];

    if (isFirstLoadRef.current) {
      return;
    }
    if (lastMsg === lastMessageRef.current && !isThinking) return;

    lastMessageRef.current = lastMsg;

    const isStreaming = lastMsg?.senderType === SenderType.BOT && lastMsg?.isFinal === false;

    // === CSKH: keep original scroll behavior (always scroll to bottom) ===
    if (activeAgent === ChatAgentType.CSKH) {
      if (isStreaming) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 250;
        if (isNearBottom) {
          scrollToBottom('auto');
        }
      } else {
        scrollToBottom('smooth');
      }
      return;
    }

    // === USER MESSAGE: scroll question to TOP (non-CSKH agents) ===
    if (lastMsg?.senderType === SenderType.USER && !lastMsg?.isHidden && !lastMsg?._isHistory) {
      isQuestionFocusedRef.current = true;

      // Padding budget so the question can sit near the top without a full-viewport gap
      container.style.paddingBottom = `${Math.round(container.clientHeight * QUESTION_FOCUS_PADDING_RATIO)}px`;

      requestAnimationFrame(() => {
        if (lastUserMsgRef.current && scrollContainerRef.current) {
          const c = scrollContainerRef.current;
          const cRect = c.getBoundingClientRect();
          const mRect = lastUserMsgRef.current.getBoundingClientRect();
          const targetScrollTop = c.scrollTop + (mRect.top - cRect.top);
          c.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        }
      });
      return;
    }

    // === BOT MESSAGE (socket / stream answer) → always scroll to bottom ===
    if (lastMsg?.senderType === SenderType.BOT) {
      isQuestionFocusedRef.current = false;
      if (container.style.paddingBottom && container.style.paddingBottom !== '16px') {
        container.style.paddingBottom = '16px';
      }
      if (isStreaming) {
        scrollToBottom('auto');
      } else {
        scrollToBottom('smooth');
      }
      return;
    }

    // Fallback (system / other): scroll to bottom
    if (isStreaming) {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 250;
      if (isNearBottom) {
        scrollToBottom('auto');
      }
    } else {
      scrollToBottom('smooth');
    }
  }, [chatLog, isThinking, scrollToBottom, isFetchingMore, recalcScrollPadding, activeAgent]);

  // Recalc padding when thinking indicator appears/disappears (rendered outside chatLog)
  useEffect(() => {
    if (!isQuestionFocusedRef.current || activeAgent === ChatAgentType.CSKH) return;
    // Wait for DOM to render the thinking indicator
    const id = requestAnimationFrame(() => {
      recalcScrollPadding();
    });
    return () => cancelAnimationFrame(id);
  }, [isThinking, recalcScrollPadding, activeAgent]);

  // Cuộn xuống bottom khi form Reconnect xuất hiện
  useEffect(() => {
    if (needsManualReconnect) {
      // Tắt thinking khi mất kết nối
      if (isThinking && setIsThinking) {
        setIsThinking(false);
      }
      // Scroll xuống để hiện form Reconnect
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 100);
    }
  }, [needsManualReconnect]);



  const handleScroll = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element || isFetchingMore) return;

    // Scroll UP → load older messages
    if (element.scrollTop <= 5 && loadMoreHistory) {
      const distFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      scrollOffsetRef.current = distFromBottom;
      isRestoringScrollRef.current = true;
      loadMoreHistory().catch(() => {
        scrollOffsetRef.current = null;
        isRestoringScrollRef.current = false;
      });
    }

    // Toggle scroll down button: exclude extra padding from question-focus mode
    // and the fixed footer spacer so the button appears when the last message
    // is still under / above the blurred input overlay.
    const distanceFromBottomForBtn = element.scrollHeight - element.scrollTop - element.clientHeight;
    const extraPadding = isQuestionFocusedRef.current
      ? Math.max(0, parseInt(element.style.paddingBottom || '16', 10) - 16)
      : 0;
    setShowScrollDown((distanceFromBottomForBtn - extraPadding) > CHAT_FOOTER_SPACER_PX);

    // Scroll DOWN → load newer messages (CSKH only, when previousCursor exists)
    if (loadNewerHistory && activeAgent === ChatAgentType.CSKH) {
      const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distanceFromBottom <= 5) {
        loadNewerHistory().catch(() => { });
      }
    }
  }, [loadMoreHistory, loadNewerHistory, isFetchingMore, activeAgent]);

  // `chatLog` is already deduplicated by `useChatHistory`

  if (hasChatHistory === null) {
    return (
      <div className="loading-indicator">
        <LoadingOutlined className="loading-indicator-icon" />
        <span className="loading-indicator-text">Đang tải</span>
      </div>
    );
  }

  // Filter out hidden messages from the log before rendering
  const filteredChatLog = (chatLog || []).filter((msg: any) => !msg.isHidden);

  // Find the actual index of the last user message in order to scroll to the correct position
  let actualLastUserIndex = -1;
  for (let i = filteredChatLog.length - 1; i >= 0; i--) {
    const sender = filteredChatLog[i]?.user || filteredChatLog[i]?.senderType;
    if (sender === SenderType.USER) {
      actualLastUserIndex = i;
      break;
    }
  }

  const renderMessage = (entry: any, index: number) => {
    const uniqueKey = entry._uniqueKey || entry.messageId || entry.id || `msg-${index}`;
    const isLastUser = index === actualLastUserIndex;

    const currTime = entry?.time || entry?.createdAt;
    const prevTime = index > 0 ? (filteredChatLog[index - 1]?.time || filteredChatLog[index - 1]?.createdAt) : null;
    const currentDate = formatChatDate(currTime);
    const prevDate = prevTime ? formatChatDate(prevTime) : null;
    const showDateDivider = Boolean(currentDate && (index === 0 || currentDate !== prevDate));

    const isCskhMode = activeAgent === ChatAgentType.CSKH;
    const groupPosition = isCskhMode ? getMessageGroupPosition(filteredChatLog, index) : undefined;
    const hasPrevSame = index > 0 && !showDateDivider && isSameSenderGroup(filteredChatLog[index - 1], entry);
    const spacingClass = isCskhMode ? getMessageSpacingClass(hasPrevSame, index === 0, showDateDivider) : '';
    const isUser = (entry?.user || entry?.senderType) === SenderType.USER;

    return (
      <React.Fragment key={uniqueKey}>
        {showDateDivider && (
          <div className="date-divider text-center my-4 font-base">
            <span className="text-sm bg-[var(--colour-semantic-neutral-surface)] text-[var(--colour-semantic-neutral-shade-md)] px-2 py-1.5 rounded-full">{currentDate}</span>
          </div>
        )}
        <div
          id={`message-${entry.messageId || entry.id}`}
          data-message-id={entry.messageId || ''}
          data-id={entry.id || ''}
          ref={isLastUser ? lastUserMsgRef : null}
          className={spacingClass || undefined}
        >
          {isUser ? (
            <ChatUser
              message={entry.text}
              isRead={entry?.isRead}
              time={entry?.time}
              messageId={entry.messageId}
              uploadStatus={entry.uploadStatus}
              uploadError={entry.uploadError}
              fullMessage={entry}
              onRetryUpload={handleRetryUpload}
              onQuote={handleQuoteMessage}
              activeAgent={activeAgent}
              isQueuing={entry.isQueuing}
              scrollToQuotedMessage={scrollToQuotedMessage}
              groupPosition={groupPosition}
            />
          ) : (
            <ChatAdmin
              onQuestionClick={handleChatSubmit}
              messageId={entry.messageId?.toString()}
              onButtonClick={(msg: string, btnId: number, isHidden?: boolean) => handleChatSubmit(msg, btnId, isHidden)}
              onPaymentAccountConfirm={(button: MessageButton) => handleChatSubmit(
                button.name, button.id, isPaymentAccountButtonHidden(button), undefined, undefined, {
                  action: 'CLICK_BUTTON', intent: button.intent || 'add_payment_account',
                  contextMap: { is_confirmed: true }, sendMessageUser: false,
                },
              )}
              onThinkingStop={() => setIsThinking?.(false)}
              chatMessage={entry}
              message={entry.text}
              handleAgentChange={handleAgentChange}
              isLastMessage={index === filteredChatLog.length - 1}
              handleForwardToAgent={handleForwardToAgent}
              handleRateMessage={handleRateMessage}
              onQuote={handleQuoteMessage}
              activeAgent={activeAgent}
              onConfirmStay={onConfirmStay}
              onConfirmLeave={onConfirmLeave}
              setToastMessage={setToastMessage}
              audioData={entry.audio}
              isAudioEnabled={isAudioEnabled}
              isBotSpeaking={isBotSpeaking}
              isAudioPlayerVisible={isAudioPlayerVisible}
              onPlayAudio={playBotAudio}
              onStopAudio={stopBotSpeaking}
              groupPosition={groupPosition}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  const renderMessagesOrEmptyState = () => {
    if (filteredChatLog.length > 0) {
      return filteredChatLog.map(renderMessage);
    }

    if (hasChatHistory === false) {
      const isCskhWelcome = activeAgent === ChatAgentType.CSKH;
      const isInstructionGuideWelcome =
        selectedPlusFeatureKey === 'manual' &&
        (activeAgent === ChatAgentType.AI || activeAgent === ChatAgentType.INSTRUCTION_MANUAL);

      return (
        <React.Fragment>
          <div className="mt-4 flex items-center justify-center gap-2 text-center font-sans text-sm font-normal leading-5 text-[#3D3D43]">
            {isCskhWelcome ? (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center [&>svg]:h-6 [&>svg]:w-6">
                <CskhHeaderIcon width={24} height={24} className="h-6 w-6" />
              </span>
            ) : (
              <span className="flex h-4 items-center [&>svg]:h-4 [&>svg]:w-auto">
                <NewIconChatbot />
              </span>
            )}
            {isInstructionGuideWelcome
              ? 'Tendoo hướng dẫn'
              : isCskhWelcome
                ? t('agents.CSKH.welcomeGreeting', 'Xin chào, tôi là nhân viên của Tendoo')
                : userName
                  ? `Xin chào ${userName}!`
                  : 'Xin chào bạn!'}
          </div>
          <div className="flex items-center justify-center text-center font-sans text-2xl font-normal leading-8 text-[#242428]">
            {isInstructionGuideWelcome
              ? 'Chọn hướng dẫn sử dụng bạn cần tìm hiểu'
              : t('common.welcomeMessage')}
          </div>

          {isInstructionGuideWelcome && onSelectInstruction && (
            <InstructionGuideGrid
              items={instructions}
              onItemClick={onSelectInstruction}
              isLoading={isInstructionsLoading}
              isThinking={isThinking}
            />
          )}

          {/* AI Hỏi đáp: onboarding cards from quick-suggestions API */}
          {!isInstructionGuideWelcome && activeAgent === ChatAgentType.AI && (
            <OnboardingFeatureGrid
              suggestions={displayQuickSuggestions}
              onSuggestionClick={handleSuggestionClick}
              isLoading={isQuickSuggestionsLoading}
              isThinking={isThinking}
            />
          )}

          {/* Other agents: default command quick actions (same as main) */}
          {!isInstructionGuideWelcome &&
            activeAgent !== ChatAgentType.AI &&
            (isCommandsLoading || (commands && commands.length > 0)) && (
            <CommandsList
              commands={commands}
              onCommandClick={handleCommandClick}
              isLoading={isCommandsLoading}
              isThinking={isThinking}
            />
          )}
        </React.Fragment>
      );
    }

    return null;
  };

  return (
    <div className="conversation-wrapper">
      <div className="messager-container">
        <div
          className={`chat-messages${activeAgent === ChatAgentType.CSKH ? ' chat-messages--cskh' : ''}`}
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
            {isFetchingMore && (
              <div className="loading-more text-center py-2 text-xs text-gray-400">
                {t('common.loadingMore')}
              </div>
            )}

            {renderMessagesOrEmptyState()}
            {isThinking && <ChatThinking activeAgent={activeAgent} />}

            {/* Reconnect message - renders like a ChatAdmin bubble at the bottom */}
            {needsManualReconnect && onManualReconnect && (
              <div className="chat-admin flex flex-col mt-[10px] w-full">
                      <ReconnectBar
                        onReconnect={onManualReconnect}
                        isConnecting={isReconnecting}
                      />
              </div>
            )}

            <div aria-hidden className="chat-messages-footer-spacer" />
            <div ref={chatRef} style={{ height: '1px' }}></div>
        </div >

        {showScrollDown && (
          <button
            className={`conversation-scroll-down-btn${isBotResponding ? ' conversation-scroll-down-btn--responding' : ''}`}
            onClick={() => scrollToBottom('smooth')}
            aria-label={isBotResponding ? 'AI đang trả lời — cuộn xuống dưới' : 'Cuộn xuống dưới'}
            type="button"
          >
            {isBotResponding ? (
              <span className="conversation-scroll-down-btn__dots" aria-hidden />
            ) : (
              <ArrowDownIcon className="w-4 h-4" />
            )}
          </button>
        )}
      </div >
    </div >
  );
};

export default Conversation;
